import OptionConst from "@/const/Option"
import { RouterConst } from "@/const/Router"
import router from "@/routers"
import { DialogService } from "@/service/DialogService"
import { useAuthStore } from "@/store/auth"
import { KoyouKeiyakTsuiseki, KoyouKeiyakuTeate, KoyouKeiyakuTime, SyuugyouBasyo } from "@/types/KoyouKeiyaku"
import { TenpuFile } from "@/types/TenpuFile"
import { last } from "lodash"
/**
 * 雇用契約詳細
 * 編集モードがcreate（新規作成）以外は元となる雇用契約のデータが自身に格納されている想定
 * 編集で使用する前に不要なデータや削除すべきデータの初期化を行うこと
 */
export class KoyouKeiyakuDetail {
  // 管理項目（内部）
  koyou_keiyaku_id: number | null = null                     // 雇用契約ID
  user_id: number | null = null                              // この雇用契約の適用対象のユーザーID（内部）※既存の雇用契約を元にした作成でのみ値をもつ
  is_sakujo = false                                          // 削除フラグ
  is_changed = false                                         // 内容変更フラグ（この雇用契約に対して内容変更された雇用契約が存在するか否か）
  created_by: number | null = null                           // 作成者ID
  updated_by: number | null = null                           // 更新者ID
  old_koyou_keiyakus: KoyouKeiyakTsuiseki[] = []    // 変更前雇用契約書
  new_koyou_keiyakus: KoyouKeiyakTsuiseki[] = []    // 変更後雇用契約書
  counts: number | null = null                               // 有効な雇用契約の枚数

  // 以下、画面の表示されている順
  // シートタイプの決定要因となる値
  syozoku_id: number | null = null   // 所属ID | 共通項目（編集対象）
  shinsei_type: number | null = null // 申請種類種別 1:新規 2:更新 3:内容変更 4:入替 | 共通項目（編集対象）
  koyou_type: number | null = null   // 雇用形態種別 1:月給 2:月給無期 3:時給 4:パート 5:パート無期 | 共通項目（編集対象）

  // 氏名・社員番号
  name: string | null = null        // 氏名 | 共通項目（編集対象）
  kana: string | null = null        // フリガナ | 共通項目（編集対象）※申請種別により分岐
  staff_code: string | null = null  // 社員番号 | 非共通項目（編集対象）※申請種別により分岐

  // 形態種別
  keitai_type: number | null = OptionConst.KOYOU_KEITAI_TYPE.SEISYAIN

  // 入社日 | 非共通項目（内部）
  // ※申請種別により分岐／既存の雇用契約を元にした作成でのみ値をもつ（値が無い場合は雇用期間の開始日がセットされる）
  nyuusya_date: Date | string | null = null

  // 請求開始予定日 | 共通項目（編集対象）
  seikyu_start_date: Date | string | null = null

  // 入替・引き継ぎ関係 | 非共通項目（編集対象） ※申請種別により分岐
  change_user_id: number | null = null            // 入替社員ユーザーID（TODO: どこでデータが入るのか確認）
  change_user_name: string | null = null          // 入替社員氏名（編集対象）
  hikitsugi_end_date: Date | string | null = null // 引継終了日

  // 協定対象派遣関係 ※所属により分岐（PGのみ／編集対象）
  is_kyoutei = false                            // 協定対象派遣労働者フラグ
  kyoutei_end_date: Date | string | null = null // 当該協定の有効期間の終了日

  // 契約期間 | 共通項目 ※形態種別による分岐あり
  keiyaku_date: Date | string | null = null   // 契約日（入社／変更日） 
  koyou_end_date: Date | string | null = null // 雇用（試用）期間 終了日 

  // 就業場所 | 共通項目
  mst_syuugyou_basyo_id: number | null = null  // 就業場所ID
  syuugyou_basyos: SyuugyouBasyo | null = null // 就業場所詳細（mst_syuugyou_basyo_idに紐づく就業場所の詳細／内部処理用／非DB項目）

  // 業務内容 | 共通項目
  work_detail1: string | null = null // 概要
  work_detail2: string | null = null // 詳細

  // 就業時間・休日関係 | 共通項目
  work_times: KoyouKeiyakuTime[] | null = []                      // 雇用契約就業時間テーブルに保存されるデータ群
  kyujitsu_zougen_notes: string | null = "※シフトによる"          // (就業時間に関する) 備考／旧・休日増減（編集対象）
  is_jikangai = false                                             // 時間外及び休日労働フラグ（編集対象）
  jikangai_notes: string | null = OptionConst.DEFAULT_NOTES_VALUE // 時間外手当の考（編集対象）

  // 週の所定労働日数・時間 | 共通項目
  // ※時と分がサーバー上でtime型に組み立てられsyotei_work_week_timesとして保存される
  syotei_work_week_days: number = 5                         // 日数（編集対象）
  syotei_work_week_times: string | Date | null = '40:00:00' // 非編集対象（内部処理用）
  syotei_work_week_times_hours: number = 40                 // 時（編集対象）| 非DB項目
  syotei_work_week_times_minutes: number = 0                // 分（編集対象）| 非DB項目

  // 所定労働時間（月）| 共通項目（編集対象）
  syotei_work_month_times: number = 160

  // 包括時間（みなし残業的なもの） | 共通項目
  // ※時と分がサーバー上でtime型に組み立てられhoukatsu_timesとして保存される
  houkatsu_times: string | Date = '00:00:00' // 非編集対象（内部処理用）
  houkatsu_times_hours: number = 0           // 時（編集対象）| 非DB項目
  houkatsu_times_minutes: number = 0         // 分（編集対象）| 非DB項目

  // 有給取得時の差し引き時間 | 共通項目
  // ※時と分がサーバー上でtime型に組み立てられsashihiki_timesとして保存される
  sashihiki_times: string | null = '00:00:00' // 非編集対象（内部処理用）
  sashihiki_times_hours: number = 0           // 時（編集対象）| 非DB項目
  sashihiki_times_minutes: number = 0         // 分（編集対象）| 非DB項目

  // 保険 | 共通項目（編集対象）
  is_koyou_hoken = false  // 雇用保険の適用フラグ 
  is_syakai_hoken = false // 社会保険の適用フラグ 

  // 年次有給休暇　| 共通項目（編集対象）※形態種別による分岐あり
  yuukyuu_days: number | null = null

  // 締日関係 | 共通項目
  shime_day: number = OptionConst.SHIME_LAST_DAY // 締め日（編集対象）
  pay_day: number = OptionConst.PAY_DAY          // 支払日（固定／表示のみ）

  // 給与関係 | 共通項目
  kihonkyuu: number = 0                     // 基本給（編集対象）
  teates: KoyouKeiyakuTeate[] | null = []   // 雇用契約手当テーブルに保存されるデータ群（編集対象）
  is_jikyuu = false                         // 時給フラグ | 共通項目（内部）※シートタイプにより分岐
  koutsuuhi: number = 0                     // 交通費 | 共通項目（編集対象）
  koutsuuhi_kazei: number = 0               // 交通費（課） | 共通項目（編集対象）
  zangyou_mikomi: number = 0                // 残業（見込み） | 共通項目（編集対象）

  // 昇給・賞与・退職金 共通項目（編集対象）
  is_syoukyuu = false    // 昇給有無 
  is_syouyo = false      // 賞与有無
  is_taisyokukin = false // 退職金有無

  // 契約更新有無種別 | 共通項目（編集対象）| 共通項目（編集対象）
  is_keiyaku_koushin: number | null = null

  // 社内メモ欄 | 共通項目（編集対象）
  syanai_notes: string | null = OptionConst.DEFAULT_NOTES_VALUE

  // 担当営業 | 共通項目
  eigyou_user_id: number | null = null   // 担当営業ID（編集対象）
  eigyou_user_name: string | null = null // 担当営業氏名 | 非DB項目

  // 雇用契約添付ファイル（履歴書等を想定） | 共通項目（編集対象）
  tenpufiles: TenpuFile[] | null = null

  // 承認関係 | 共通項目 ※詳細画面にて編集可
  submitted_date: Date | string | null = null // 提出日
  submitted_user_id: number | null = null     // 提出者ID
  submitted_user_name: string | null = null   // 提出者氏名
  syounin_status_type: number | null = null   // 承認ステータス種別
  readedDeniedMessage = false                         // 否認された場合に提出者がその後の処理を行ったか
  fusaiyou_notes: string | null = null        // 不採用理由

  // 本人承認関係 | 共通項目（編集対象） ※詳細画面にて編集可
  honnin_status_type: number | null = null                      // 本人ステータス種別
  honnin_notes: string | null = OptionConst.DEFAULT_NOTES_VALUE // 本人通知欄（内容変更の場合のみに、変更内容を記述する）

  // 雇用契約使用済みフラグ
  koyoukeiyaku_is_used: boolean = false;

  // エラー配列
  errors: any[string] = [{}]

  setYuukyuuDays() {
    this.yuukyuu_days = this.yuukyuu_days === null ? 10 : this.yuukyuu_days;
  }

  /**
   * 新規作成時の初期化
   */
  public async initForCreate() {
    // 所属として自身と同じ所属をデフォルト値として設定する
    const authStore = useAuthStore();
    if (authStore.user?.mst_syozoku_id) {
      this.syozoku_id = authStore.user.mst_syozoku_id;
    }

    // 新規作成では入社のみが選択可能なのでその値を設定
    this.shinsei_type = OptionConst.KOYOU_SHINSEI_TYPE.ENTRY

    // 初期値として月給を設定
    this.koyou_type = OptionConst.KOYOU_TYPE.GEKKYUU;
  }

  /**
   * 既存の雇用契約から新規作成する場合の初期化
   * 既存の雇用契約およびそこから派生するテーブルのデータと関連を断つという観点で実装すること
   */
  public async initForCreateFromModeCreate() {
    // 管理項目の初期化
    this.koyou_keiyaku_id = null
    this.user_id = null
    this.is_sakujo = false
    this.is_changed = false
    this.created_by = null
    this.updated_by = null
    this.new_koyou_keiyakus = []
    this.clearOldKoyouKeiyakus()
    this.counts = null

    // 入替関係
    this.change_user_id = null
    this.change_user_name = null
    this.hikitsugi_end_date = null

    // 社内メモ欄
    this.syanai_notes = OptionConst.DEFAULT_NOTES_VALUE

    // 雇用契約添付ファイル
    this.tenpufiles = null

    // 承認関係
    this.submitted_date = null
    this.submitted_user_id = null
    this.submitted_user_name = null
    this.syounin_status_type = null
    this.readedDeniedMessage = false
    this.fusaiyou_notes = null

    // 本人承認関係
    this.honnin_status_type = null
    this.honnin_notes = OptionConst.DEFAULT_NOTES_VALUE

    // 契約情報のうち、引き継がないデータの初期化（TODO: 引き継がないデータの資料があるか確認）
    this.seikyu_start_date = null
    this.kyoutei_end_date = null
    this.keiyaku_date = null
    this.koyou_end_date = null
    this.is_taisyokukin = false

    // すでにセットされている就業時間のうち、既存の雇用契約と紐づくidをnullにする
    // ※雇用契約IDはサーバー側でセットされ、主キーはupsert時に自動付番される
    if (this.work_times?.length) {
      this.work_times.forEach(times => {
        times.koyou_keiyaku_time_id = null
        times.koyou_keiyaku_id = null
      });
    }

    // すでにセットされている手当のうち、既存の雇用契約と紐づくidをnullにする
    // ※雇用契約IDはサーバー側でセットされ、主キーはupsert時に自動付番される
    if (this.teates?.length) {
      this.teates.forEach(teate => {
        teate.koyou_keiyaku_teate_id = null
        teate.koyou_keiyaku_id = null
      });
    }
  }

  /**
   * 雇用契約メニューに遷移させる
   * 主にエラー時に使用
   */
  public async backMenu() {
    // 内容変更で既存データがない場合はエラーとして扱う
    // ※変更前後の情報の整合性が取れなくなり追跡できなくなるため
    DialogService.error({
      title: '初期化エラー',
      text: '恐れ入りますが最初から操作をやりなおしてください'
    }).then(() => {
      router.push(RouterConst.KOYOUKEIYAKU_MENU)
    })
  }
  /**
   * 申請種別、雇用形態種別の変更によりシートタイプが変更される場合の初期化
   * modeがfromの場合、initForCreateFromModeCreateが実行されたあとに本処理が実行される想定で初期化する項目を決定すること
   * @param sheetType シートタイプ
   * @param mode      編集モード（create/from/edit）
   * @param lastData  元となるデータ
   * @returns 
   */
  public async initBySheetType(mode: string, lastData?: KoyouKeiyakuDetail | null) {
    // 入替えや引き継ぎに関する項目は常に初期化
    this.change_user_id = null
    this.change_user_name = null
    this.hikitsugi_end_date = null

    if (this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.CHANGE && !lastData) {
      await this.backMenu()
      return
    }

    if (
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.ENTRY ||
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.REPLACE ||
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.REPLACE_ENTRY ||
      (
        this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.ADD &&
        mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.FROM
      )
    ) {
      // 申請タイプが新規／入替／新規（入替） ＝元となる雇用契約の対象者とは別の人の場合
      // もしくは既存ユーザーに対する追加の雇用契約の新規作成
      // 元となった雇用契約から引き継がない情報の初期化
      this.nyuusya_date = null
      this.user_id = null
      this.name = null
      this.kana = null
      this.staff_code = null
    } else if (
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.CHANGE ||
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.UPDATE ||
      (
        this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.ADD &&
        mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.EDIT
      ) 
    ) {
      // 申請タイプが内容変更／更新／追加 ＝元となる雇用契約の対象者と同一の場合
      if (!lastData || !lastData.koyou_keiyaku_id) {
        await this.backMenu()
        return
      }

      this.nyuusya_date = lastData.nyuusya_date
      this.user_id = lastData.user_id
      this.name = lastData.name
      this.kana = lastData.kana
      this.staff_code = lastData.staff_code
    }

    // 申請タイプが内容変更の場合 変更前の雇用契約書を主（基準）となる雇用契約として保持させる
    if (this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.CHANGE) {
      if (!lastData || !lastData.koyou_keiyaku_id) {
        await this.backMenu()
        return
      }

      if (this.old_koyou_keiyakus.length < 1) {
        this.old_koyou_keiyakus = [{
          koyou_keiyaku_tsuiseki_id: null,
          old_koyou_keiyaku_id: lastData.koyou_keiyaku_id,
          new_koyou_keiyaku_id: null,
          is_main: true
        }];
      }
    } else {
      // 変更以外の場合はクリア
      this.clearOldKoyouKeiyakus()
    }

    // 申請タイプが入替系の場合、誰からの入替えか保持させる
    if (
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.REPLACE_ENTRY ||
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.REPLACE
    ) {
      if (lastData) {
        this.change_user_id = lastData.user_id
        this.change_user_name = lastData.name
      }
    }

    // 申請タイプが新規／新規（入替）の場合は契約日を入社日として扱う
    if (
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.ENTRY ||
      this.shinsei_type === OptionConst.KOYOU_SHINSEI_TYPE.REPLACE_ENTRY
    ) {
      this.nyuusya_date = this.keiyaku_date ? this.keiyaku_date : null;
    }

    // 時給判定
    if (
      this.koyou_type === OptionConst.KOYOU_TYPE.JIKYUU ||
      this.koyou_type === OptionConst.KOYOU_TYPE.PART ||
      this.koyou_type === OptionConst.KOYOU_TYPE.PART_MUKI
    ) {
      this.is_jikyuu = true
    } else {
      this.is_jikyuu = false
    }

    // 有給日数の初期化
    if (
      this.koyou_type === OptionConst.KOYOU_TYPE.PART ||
      this.koyou_type === OptionConst.KOYOU_TYPE.PART_MUKI
    ) {
      this.yuukyuu_days = 0
    } else {
      this.setYuukyuuDays()
    }

    // 月給以外は手当無し
    if (
      this.koyou_type !== OptionConst.KOYOU_TYPE.GEKKYUU &&
      this.koyou_type !== OptionConst.KOYOU_TYPE.GEKKYUU_MUKI
    ) {
      this.teates = []
    }

    // 有期／無期
    this.koyou_end_date = null
    if (
      this.koyou_type === OptionConst.KOYOU_TYPE.GEKKYUU_MUKI ||
      this.koyou_type === OptionConst.KOYOU_TYPE.PART_MUKI
    ) {
      // 契約終了日を無し（＝無期）とし、更新有無を無しとする
      this.is_keiyaku_koushin = 0
    } else {
      this.is_keiyaku_koushin = 1
    }

    // 協定対象派遣に関する初期化
    this.kyoutei_end_date = null
    if (
      this.keitai_type !== OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_FULLTIME &&
      this.keitai_type !== OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_PART
    ) {
      this.is_kyoutei = false
    } else {
      this.is_kyoutei = true
    }

    // 退職金
    // TODO: 所属による分岐のため外販時には無くす
    if (this.syozoku_id !== OptionConst.SYOZOKU.PG) {
      this.is_taisyokukin = false
    } else {
      this.is_taisyokukin = true
    }

    // 時間外及び休日労働
    // TODO: 所属による分岐のため外販時には無くす
    if (this.syozoku_id === OptionConst.SYOZOKU.PM) {
      this.is_jikangai = true
    } else {
      this.is_jikangai = true
    }
  }
  public clearOldKoyouKeiyakus() {
    this.old_koyou_keiyakus = []
  }
}